import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';

import { Fonts } from '@/constants/theme';
import { fetchAssignmentsForTeacher, type AssignmentListItem } from '@/lib/assignments-admin';
import { buildOmrDocument, type OmrStudent } from '@/lib/omr-template';
import {
  createDraftFromQuestion,
  createQuestionSet,
  fetchQuestionsForSet,
  fetchTeacherQuestionSets,
  type DraftQuestion,
  generateMcqDrafts,
  type McqGenerationDifficulty,
  type QuestionSetStatus,
  type QuestionListItem,
  syncQuestionsBatch,
  type TeacherQuestionSetListItem,
  updateQuestionSetStatus,
} from '@/lib/question-sets';
import { fetchStudentsForClass, type StudentListItem } from '@/lib/students-admin';
import { useAuth } from '@/providers/auth-provider';

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function makeDraftId() {
  return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function createEmptyDraft(): DraftQuestion {
  return {
    id: makeDraftId(),
    prompt: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: 'A',
    explanation: '',
    marks: 1,
  };
}

function hasDraftContent(draft: DraftQuestion) {
  return Boolean(
    draft.prompt.trim() ||
      draft.optionA.trim() ||
      draft.optionB.trim() ||
      draft.optionC.trim() ||
      draft.optionD.trim() ||
      draft.explanation.trim()
  );
}

function isDraftComplete(draft: DraftQuestion) {
  return Boolean(
    draft.prompt.trim() &&
      draft.optionA.trim() &&
      draft.optionB.trim() &&
      draft.optionC.trim() &&
      draft.optionD.trim()
  );
}

function formatDate(date: Date) {
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatQuestionSetStatus(status: QuestionSetStatus) {
  if (status === 'published') return 'Published';
  if (status === 'archived') return 'Archived';
  return 'Draft';
}

function getTransferredSetNote(
  set: Pick<TeacherQuestionSetListItem, 'teacher_id' | 'teacher_name'>,
  currentTeacherId?: string | null
) {
  if (!currentTeacherId || set.teacher_id === currentTeacherId) {
    return null;
  }

  const teacherName = set.teacher_name?.trim();
  return teacherName ? `Created by ${teacherName}` : 'Created by previous teacher';
}

function isSupportedOmrQuestionCount(questionCount: number) {
  return questionCount >= 5 && questionCount <= 100 && questionCount % 5 === 0;
}

const questionSetStatusOptions: Array<{
  value: QuestionSetStatus;
  label: string;
}> = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export default function TeacherSetsScreen() {
  const { profile } = useAuth();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [questionSets, setQuestionSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuestionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [setModalVisible, setSetModalVisible] = useState(false);
  const [submittingSet, setSubmittingSet] = useState(false);
  const [setTitle, setSetTitle] = useState('');
  const [setDescription, setSetDescription] = useState('');
  const [selectedAssignmentKey, setSelectedAssignmentKey] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [addMcqModalVisible, setAddMcqModalVisible] = useState(false);
  const [drafts, setDrafts] = useState<DraftQuestion[]>([]);
  const [builderMode, setBuilderMode] = useState<'ai' | 'manual'>('manual');
  const [aiQuestionCount, setAiQuestionCount] = useState('5');
  const [aiTopic, setAiTopic] = useState('');
  const [aiDifficulty, setAiDifficulty] = useState<McqGenerationDifficulty>('medium');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [batchFeedback, setBatchFeedback] = useState<string | null>(null);
  const [savingBatch, setSavingBatch] = useState(false);
  const [updatingSetStatus, setUpdatingSetStatus] = useState(false);
  const [omrModalVisible, setOmrModalVisible] = useState(false);
  const [omrSet, setOmrSet] = useState<TeacherQuestionSetListItem | null>(null);
  const [omrStudents, setOmrStudents] = useState<StudentListItem[]>([]);
  const [omrStudentsLoading, setOmrStudentsLoading] = useState(false);
  const [omrGenerating, setOmrGenerating] = useState(false);
  const [omrTestDate, setOmrTestDate] = useState(formatDate(new Date()));

  const assignmentOptions = useMemo(
    () =>
      assignments.map((assignment) => ({
        ...assignment,
        key: `${assignment.class_id}:${assignment.subject_id}`,
        label: `${assignment.class_label} - ${assignment.subject_label}`,
      })),
    [assignments]
  );
  const selectedAssignment = useMemo(
    () => assignmentOptions.find((option) => option.key === selectedAssignmentKey) ?? null,
    [assignmentOptions, selectedAssignmentKey]
  );
  const activeSet = useMemo(
    () => questionSets.find((set) => set.id === selectedSetId) ?? null,
    [questionSets, selectedSetId]
  );

  const filteredSets = useMemo(() => {
    if (!searchQuery.trim()) return questionSets;
    const query = searchQuery.toLowerCase();
    return questionSets.filter(
      (set) =>
        set.title.toLowerCase().includes(query) ||
        set.subject_label.toLowerCase().includes(query) ||
        set.class_label.toLowerCase().includes(query)
    );
  }, [questionSets, searchQuery]);

  const totalSets = questionSets.length;
  const totalQuestions = useMemo(
    () => questionSets.reduce((sum, set) => sum + (set.question_count || 0), 0),
    [questionSets]
  );
  const totalAssignments = assignments.length;
  const hasDraftsReady = useMemo(() => drafts.some((draft) => hasDraftContent(draft)), [drafts]);
  const omrQuestionCountSupported = omrSet ? isSupportedOmrQuestionCount(omrSet.question_count) : false;

  useEffect(() => {
    if (!isDesktop || loading || selectedSetId || questionSets.length === 0) return;
    setSelectedSetId(questionSets[0].id);
  }, [isDesktop, loading, questionSets, selectedSetId]);

  const loadData = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    try {
      const [assignmentRows, setRows] = await Promise.all([
        fetchAssignmentsForTeacher(profile.id),
        fetchTeacherQuestionSets(profile.id),
      ]);
      setAssignments(assignmentRows);
      setQuestionSets(setRows);
    } catch (error) {
      Alert.alert('Unable to load sets', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  const loadQuestions = useCallback(async () => {
    if (!selectedSetId) {
      setQuestions([]);
      return;
    }

    setQuestionLoading(true);
    try {
      const rows = await fetchQuestionsForSet(selectedSetId);
      setQuestions(rows);
    } catch (error) {
      Alert.alert('Unable to load questions', getErrorMessage(error));
    } finally {
      setQuestionLoading(false);
    }
  }, [selectedSetId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedSetId) {
      void loadQuestions();
    }
  }, [selectedSetId, loadQuestions]);

  const openSetModal = () => {
    setSetTitle('');
    setSetDescription('');
    setSelectedAssignmentKey(assignmentOptions[0]?.key ?? '');
    setSetModalVisible(true);
  };

  const handleCreateSet = async () => {
    if (!profile?.id || !profile.school_id) return;
    const assignment = assignmentOptions.find((item) => item.key === selectedAssignmentKey);
    if (!setTitle.trim() || !assignment) return;

    setSubmittingSet(true);
    try {
      const questionSetId = await createQuestionSet({
        classId: assignment.class_id,
        description: setDescription,
        schoolId: profile.school_id,
        subjectId: assignment.subject_id,
        teacherId: profile.id,
        title: setTitle,
      });
      setSetModalVisible(false);
      await loadData();
      setSelectedSetId(questionSetId);
    } catch (error) {
      Alert.alert('Error', getErrorMessage(error));
    } finally {
      setSubmittingSet(false);
    }
  };

  const openAddMcqModal = async (setId: string) => {
    setSelectedSetId(setId);
    setBuilderLoading(true);
    setDrafts([]);
    setBuilderMode('manual');
    setAiQuestionCount('5');
    setAiTopic('');
    setAiDifficulty('medium');
    setAiInstructions('');
    setBatchFeedback(null);
    setAddMcqModalVisible(true);
    try {
      const existingQuestions = await fetchQuestionsForSet(setId);
      setDrafts(
        existingQuestions.length > 0
          ? existingQuestions.map((question) => createDraftFromQuestion(question))
          : [createEmptyDraft()]
      );
    } catch (error) {
      setDrafts([createEmptyDraft()]);
      setBatchFeedback(getErrorMessage(error));
    } finally {
      setBuilderLoading(false);
    }
  };

  const handleRemoveDraft = (index: number) => {
    if (drafts.length <= 1) return;
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDraft = (index: number, fields: Partial<DraftQuestion>) => {
    setDrafts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...fields };
      return updated;
    });
  };

  const handleSaveAllMcqs = async () => {
    if (!selectedSetId || !profile?.school_id) return;
    const filledDrafts = drafts.filter(hasDraftContent);

    if (filledDrafts.length === 0) {
      setBatchFeedback('Add at least one complete MCQ before saving.');
      return;
    }

    const incompleteIndex = filledDrafts.findIndex((draft) => !isDraftComplete(draft));

    if (incompleteIndex >= 0) {
      setBatchFeedback(`Question ${incompleteIndex + 1} is incomplete. Fill the prompt and all four options.`);
      return;
    }

    setSavingBatch(true);
    setBatchFeedback(null);
    try {
      await syncQuestionsBatch(filledDrafts, selectedSetId, profile.school_id);
      setAddMcqModalVisible(false);
      setDrafts([]);
      await Promise.all([loadData(), loadQuestions()]);
    } catch (error) {
      Alert.alert('Error', getErrorMessage(error));
    } finally {
      setSavingBatch(false);
    }
  };

  const handleGenerateAiDrafts = async () => {
    if (!selectedSetId) {
      return;
    }

    const count = parseInt(aiQuestionCount, 10);

    if (!Number.isInteger(count) || count < 1 || count > 20) {
      setBatchFeedback('Choose a question count between 1 and 20.');
      return;
    }

    setAiGenerating(true);
    setBatchFeedback(null);

    try {
      const generatedDrafts = await generateMcqDrafts({
        count,
        difficulty: aiDifficulty,
        instructions: aiInstructions,
        questionSetId: selectedSetId,
        topic: aiTopic,
      });

      setDrafts(generatedDrafts);
      setBatchFeedback('AI generated the MCQs. Review and edit anything before saving.');
    } catch (error) {
      setBatchFeedback(getErrorMessage(error));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUpdateSetStatus = async (status: QuestionSetStatus) => {
    if (!activeSet || !profile?.school_id || activeSet.status === status) {
      return;
    }

    setUpdatingSetStatus(true);
    try {
      await updateQuestionSetStatus({
        questionSetId: activeSet.id,
        schoolId: profile.school_id,
        status,
      });

      setQuestionSets((prev) =>
        prev.map((set) => (set.id === activeSet.id ? { ...set, status } : set))
      );
      setBatchFeedback(`Set status updated to ${status}.`);
    } catch (error) {
      Alert.alert('Unable to update set status', getErrorMessage(error));
    } finally {
      setUpdatingSetStatus(false);
    }
  };

  const openOmrModal = async (set: TeacherQuestionSetListItem) => {
    setOmrSet(set);
    setOmrStudents([]);
    setOmrTestDate(formatDate(new Date()));
    setOmrModalVisible(true);
    setOmrStudentsLoading(true);

    try {
      const students = await fetchStudentsForClass(set.class_id, set.school_id);
      setOmrStudents(students);
    } catch (error) {
      Alert.alert('Error loading students', getErrorMessage(error));
    } finally {
      setOmrStudentsLoading(false);
    }
  };

  const handleGenerateOmr = async () => {
    if (!omrSet || omrStudents.length === 0) {
      Alert.alert('No students', 'There are no students in this class to generate OMR sheets for.');
      return;
    }

    if (omrSet.question_count === 0) {
      Alert.alert('No questions', 'Please add questions to this MCQ set before generating OMR sheets.');
      return;
    }

    if (!isSupportedOmrQuestionCount(omrSet.question_count)) {
      Alert.alert(
        'Unsupported question count',
        'OMR sheets can only be generated for sets with 5, 10, 15, up to 100 questions.'
      );
      return;
    }

    setOmrGenerating(true);
    try {
      const now = new Date();
      const issuedAt = now.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      const students: OmrStudent[] = omrStudents.map((student) => ({
        studentId: student.id,
        studentName: student.full_name || `Student ${student.roll_number}`,
        rollNumber: student.roll_number,
      }));

      const html = buildOmrDocument({
        subject: omrSet.subject_label,
        dateLabel: omrTestDate,
        testDate: omrTestDate,
        issuedAt,
        examId: omrSet.id,
        totalQuestions: omrSet.question_count,
        students,
      });

      if (Platform.OS === 'web') {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(html);
          printWindow.document.close();
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
          };
          setTimeout(() => {
            try {
              printWindow.focus();
              printWindow.print();
            } catch {
              // window may already be closed
            }
          }, 1000);
        } else {
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `OMR_Sheets_${omrSet.title.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          Alert.alert('Downloaded', 'OMR sheets downloaded as HTML. Open the file and print it (Ctrl+P).');
        }
      } else {
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: `OMR Sheets - ${omrSet.title}`,
            UTI: 'com.adobe.pdf',
          });
        } else {
          Alert.alert('PDF Generated', `OMR sheets saved at:\n${uri}`);
        }
      }

      setOmrModalVisible(false);
    } catch (error) {
      Alert.alert('Generation failed', getErrorMessage(error));
    } finally {
      setOmrGenerating(false);
    }
  };

  const batchBuilderContent = (
    <View style={styles.batchContainer}>
        <View style={styles.batchH}>
        <Pressable style={styles.batchCloseBtn} onPress={() => setAddMcqModalVisible(false)}>
          <MaterialIcons name="close" size={22} color="#163828" />
        </Pressable>
        <View style={styles.batchHeaderCopy}>
          <Text style={styles.batchEyebrow}>Question builder</Text>
          <Text style={styles.batchHT}>Manage MCQs</Text>
          <Text style={styles.batchHS}>
            Teachers can write questions manually or generate a draft with AI, then review everything before saving.
          </Text>
        </View>
        <Pressable
          style={[styles.batchSavePill, savingBatch && styles.disabledButton]}
          onPress={() => void handleSaveAllMcqs()}
          disabled={savingBatch}
        >
          {savingBatch ? (
            <ActivityIndicator color="white" size="small" />
          ) : (
            <Text style={styles.saveBold}>Save</Text>
          )}
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={styles.batchS} keyboardShouldPersistTaps="handled">
        {activeSet && (
          <View style={styles.batchContextCard}>
            <View style={styles.batchContextTop}>
              <MaterialIcons name="library-books" size={18} color="#163828" />
              <Text style={styles.batchContextLabel}>Current set</Text>
            </View>
            <Text style={styles.batchContextTitle}>{activeSet.title}</Text>
            <Text style={styles.batchContextMeta}>
              {activeSet.subject_label} · {activeSet.class_label}
            </Text>
            {getTransferredSetNote(activeSet, profile?.id) ? (
              <Text style={styles.batchContextOwnerNote}>
                {getTransferredSetNote(activeSet, profile?.id)}
              </Text>
            ) : null}
            <View style={styles.batchStatusSection}>
              <Text style={styles.batchStatusLabel}>Set visibility</Text>
              <View style={styles.batchStatusRow}>
                {questionSetStatusOptions.map((option) => {
                  const isActive = activeSet.status === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => void handleUpdateSetStatus(option.value)}
                      disabled={updatingSetStatus}
                      style={[
                        styles.batchStatusChip,
                        isActive && styles.batchStatusChipActive,
                        updatingSetStatus && styles.disabledButton,
                      ]}
                    >
                      <Text
                        style={[
                          styles.batchStatusChipText,
                          isActive && styles.batchStatusChipTextActive,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        <View style={styles.builderModeCard}>
          <Text style={styles.builderModeLabel}>Question input mode</Text>
          <View style={styles.builderModeRow}>
            <Pressable
              onPress={() => setBuilderMode('manual')}
              style={[styles.builderModeChip, builderMode === 'manual' && styles.builderModeChipActive]}
            >
              <MaterialIcons
                color={builderMode === 'manual' ? '#F7F4EE' : '#163828'}
                name="edit-note"
                size={18}
              />
              <Text
                style={[
                  styles.builderModeChipText,
                  builderMode === 'manual' && styles.builderModeChipTextActive,
                ]}
              >
                Manual
              </Text>
            </Pressable>

            <Pressable
              onPress={() => setBuilderMode('ai')}
              style={[styles.builderModeChip, builderMode === 'ai' && styles.builderModeChipActive]}
            >
              <MaterialIcons
                color={builderMode === 'ai' ? '#F7F4EE' : '#163828'}
                name="auto-awesome"
                size={18}
              />
              <Text
                style={[
                  styles.builderModeChipText,
                  builderMode === 'ai' && styles.builderModeChipTextActive,
                ]}
              >
                Generate with AI
              </Text>
            </Pressable>
          </View>
        </View>

        {builderMode === 'ai' ? (
          <View style={styles.aiBuilderCard}>
            <View style={styles.aiBuilderHead}>
              <Text style={styles.aiBuilderTitle}>Generate a question draft</Text>
              <Text style={styles.aiBuilderHint}>
                Use the set title, class, and subject as context. You can still edit every generated MCQ before saving.
              </Text>
            </View>

            <InputField
              label="TOPIC OR CHAPTER"
              value={aiTopic}
              onChangeText={setAiTopic}
              placeholder="e.g. Photosynthesis, Algebra basics, Mughal Empire"
            />

            <View style={styles.aiFieldRow}>
              <View style={styles.aiFieldCol}>
                <InputField
                  label="QUESTION COUNT"
                  value={aiQuestionCount}
                  onChangeText={setAiQuestionCount}
                  keyboardType="number-pad"
                />
              </View>
              <View style={styles.aiFieldColWide}>
                <Text style={styles.fldL}>DIFFICULTY</Text>
                <View style={styles.aiDifficultyRow}>
                  {(['easy', 'medium', 'hard', 'mixed'] as const).map((difficulty) => (
                    <Pressable
                      key={difficulty}
                      onPress={() => setAiDifficulty(difficulty)}
                      style={[
                        styles.aiDifficultyChip,
                        aiDifficulty === difficulty && styles.aiDifficultyChipActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.aiDifficultyChipText,
                          aiDifficulty === difficulty && styles.aiDifficultyChipTextActive,
                        ]}
                      >
                        {difficulty[0]?.toUpperCase()}
                        {difficulty.slice(1)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            </View>

            <InputField
              label="EXTRA INSTRUCTIONS"
              value={aiInstructions}
              onChangeText={setAiInstructions}
              multiline
              placeholder="e.g. Keep it grade 8 level, include one application-based question, avoid repeated options"
            />

            <Pressable
              style={[styles.aiGenerateBtn, aiGenerating && styles.disabledButton]}
              onPress={() => void handleGenerateAiDrafts()}
              disabled={aiGenerating}
            >
              {aiGenerating ? (
                <ActivityIndicator color="#F7F4EE" />
              ) : (
                <>
                  <MaterialIcons name="auto-awesome" size={18} color="#F7F4EE" />
                  <Text style={styles.aiGenerateBtnText}>Generate MCQs</Text>
                </>
              )}
            </Pressable>
          </View>
        ) : null}

        {batchFeedback ? (
          <View style={styles.batchFeedbackCard}>
            <Text style={styles.batchFeedbackText}>{batchFeedback}</Text>
          </View>
        ) : null}

        {builderLoading ? (
          <View style={styles.batchLoadingCard}>
            <ActivityIndicator color="#163828" />
            <Text style={styles.batchLoadingText}>Loading existing questions...</Text>
          </View>
        ) : builderMode === 'manual' || hasDraftsReady ? (
          <>
            {drafts.map((draft, index) => (
              <View key={draft.id} style={styles.batchCard}>
                <View style={styles.bcH}>
                  <View style={styles.bcHTWrap}>
                    <Text style={styles.bcHT}>Question {index + 1}</Text>
                    {draft.questionId ? (
                      <View style={styles.existingQuestionChip}>
                        <Text style={styles.existingQuestionChipText}>Saved</Text>
                      </View>
                    ) : null}
                  </View>
                  {drafts.length > 1 && !draft.questionId ? (
                    <Pressable onPress={() => handleRemoveDraft(index)}>
                      <MaterialIcons name="delete" size={20} color="#EF5350" />
                    </Pressable>
                  ) : null}
                </View>

                <InputField
                  label="PROMPT"
                  value={draft.prompt}
                  onChangeText={(value) => updateDraft(index, { prompt: value })}
                  multiline
                />

                <View style={styles.optRow}>
                  <View style={styles.optI}>
                    <InputField
                      label="A"
                      value={draft.optionA}
                      onChangeText={(value) => updateDraft(index, { optionA: value })}
                    />
                  </View>
                  <View style={styles.optI}>
                    <InputField
                      label="B"
                      value={draft.optionB}
                      onChangeText={(value) => updateDraft(index, { optionB: value })}
                    />
                  </View>
                  <View style={styles.optI}>
                    <InputField
                      label="C"
                      value={draft.optionC}
                      onChangeText={(value) => updateDraft(index, { optionC: value })}
                    />
                  </View>
                  <View style={styles.optI}>
                    <InputField
                      label="D"
                      value={draft.optionD}
                      onChangeText={(value) => updateDraft(index, { optionD: value })}
                    />
                  </View>
                </View>

                <View style={styles.bcF}>
                  <View style={styles.correctB}>
                    <View style={styles.miniRow}>
                      {(['A', 'B', 'C', 'D'] as const).map((option) => (
                        <Pressable
                          key={option}
                          onPress={() => updateDraft(index, { correctOption: option })}
                          style={[styles.miniBtn, draft.correctOption === option && styles.miniBtnA]}
                        >
                          <Text style={[styles.miniBtnT, draft.correctOption === option && styles.miniBtnTA]}>
                            {option}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                  <View style={styles.marksB}>
                    <InputField
                      label="MARK"
                      value={String(draft.marks)}
                      onChangeText={(value) => updateDraft(index, { marks: parseInt(value, 10) || 1 })}
                      keyboardType="number-pad"
                    />
                  </View>
                </View>
              </View>
            ))}

            <Pressable style={styles.batchPlus} onPress={() => setDrafts((prev) => [...prev, createEmptyDraft()])}>
              <Text style={styles.batchPlusT}>+ Add another</Text>
            </Pressable>
          </>
        ) : null}
      </ScrollView>

      <View style={styles.batchAction}>
        <Pressable style={styles.batchBtn} onPress={() => void handleSaveAllMcqs()}>
          {savingBatch ? (
            <ActivityIndicator color="white" />
          ) : (
            <Text style={styles.batchBtnT}>Save all questions</Text>
          )}
        </Pressable>
      </View>
    </View>
  );

  const sharedModals = (
    <>
      <Modal
        visible={setModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSetModalVisible(false)}
      >
        <View style={[styles.modalBackdrop, isDesktop && styles.modalBackdropDesktop]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setSetModalVisible(false)} />
          <View style={[styles.bsSheet, isDesktop && styles.bsSheetDesktop]}>
            <View style={styles.bsHandle} />

            <View style={styles.modalHeader}>
              <View style={styles.modalHeaderIcon}>
                <MaterialIcons name="note-add" size={22} color="#163828" />
              </View>
              <View style={styles.modalHeaderCopy}>
                <Text style={styles.modalEyebrow}>Create a paper</Text>
                <Text style={styles.modalTitle}>New Set</Text>
                <Text style={styles.modalSubtitle}>
                  Give the paper a title, describe the purpose, and attach it to a class and subject.
                </Text>
              </View>
            </View>

            <View style={[styles.setModalBody, isDesktop && styles.setModalBodyDesktop]}>
              <View style={[styles.setModalColumn, isDesktop && styles.setModalColumnPrimary]}>
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionLabel}>Set details</Text>
                  <InputField label="TITLE" value={setTitle} onChangeText={setSetTitle} />
                  <InputField
                    label="DESCRIPTION"
                    value={setDescription}
                    onChangeText={setSetDescription}
                    multiline
                  />
                </View>
              </View>

              <View style={[styles.setModalColumn, isDesktop && styles.setModalColumnSecondary]}>
                <View style={styles.modalSection}>
                  <View style={styles.modalSectionHead}>
                    <Text style={styles.modalSectionLabel}>Assignment</Text>
                    <Text style={styles.modalSectionHint}>Choose the class and subject for this set.</Text>
                  </View>
                  <View style={styles.assGrid}>
                    {assignmentOptions.map((option) => {
                      const isSelected = selectedAssignmentKey === option.key;
                      return (
                        <Pressable
                          key={option.key}
                          onPress={() => setSelectedAssignmentKey(option.key)}
                          style={[styles.assBtn, isSelected && styles.assBtnA]}
                        >
                          <MaterialIcons
                            name={isSelected ? 'check-circle' : 'radio-button-unchecked'}
                            size={16}
                            color={isSelected ? '#163828' : '#8A968B'}
                          />
                          <Text style={[styles.assBtnT, isSelected && styles.assBtnTA]}>{option.label}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>

                {selectedAssignment && (
                  <View style={styles.selectionCard}>
                    <Text style={styles.selectionLabel}>Selected assignment</Text>
                    <Text style={styles.selectionTitle}>{selectedAssignment.class_label}</Text>
                    <Text style={styles.selectionMeta}>{selectedAssignment.subject_label}</Text>
                  </View>
                )}

                {isDesktop && (
                  <View style={styles.setSummaryCard}>
                    <Text style={styles.setSummaryLabel}>What happens next</Text>
                    <Text style={styles.setSummaryText}>
                      After creating the set, you can open it immediately to manage MCQs or generate OMR sheets.
                    </Text>
                  </View>
                )}
              </View>
            </View>

            <View style={styles.bsActions}>
              <Pressable style={styles.bsCancel} onPress={() => setSetModalVisible(false)}>
                <Text style={styles.bsCancelT}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.bsSave, submittingSet && styles.disabledButton]}
                onPress={() => void handleCreateSet()}
                disabled={submittingSet}
              >
                {submittingSet ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.bsSaveT}>Create set</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={addMcqModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setAddMcqModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setAddMcqModalVisible(false)} />
          {batchBuilderContent}
        </View>
      </Modal>

      <Modal visible={omrModalVisible} animationType="slide">
        <View style={styles.omrContainer}>
          <View style={styles.omrHeader}>
            <Pressable onPress={() => setOmrModalVisible(false)} style={styles.omrCloseBtn}>
              <MaterialIcons name="close" size={24} color="#1B5E20" />
            </Pressable>
            <View style={styles.omrHeaderCenter}>
              <Text style={styles.omrHeaderTitle}>Generate OMR Sheets</Text>
              <Text style={styles.omrHeaderSub}>Prepare the final roster before export.</Text>
            </View>
            <View style={{ width: 40 }} />
          </View>

          <ScrollView contentContainerStyle={styles.omrScroll} showsVerticalScrollIndicator={false}>
            <View style={styles.omrHeroPanel}>
              <Text style={styles.omrHeroEyebrow}>Print-ready export</Text>
              <Text style={styles.omrHeroTitle}>{omrSet ? omrSet.title : 'MCQ set'}</Text>
              <Text style={styles.omrHeroCopy}>
                Each student gets a personalized sheet with QR data, roll number, and the question count tied to this
                set.
              </Text>
            </View>

            {omrSet && (
              <View style={styles.omrInfoCard}>
                <View style={styles.omrInfoRow}>
                  <View style={styles.omrInfoIcon}>
                    <MaterialIcons name="description" size={20} color="#1B5E20" />
                  </View>
                  <View style={styles.omrInfoText}>
                    <Text style={styles.omrInfoLabel}>MCQ Set</Text>
                    <Text style={styles.omrInfoValue}>{omrSet.title}</Text>
                  </View>
                </View>
                <View style={styles.omrInfoDivider} />
                <View style={styles.omrInfoGrid}>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Subject</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.subject_label}</Text>
                  </View>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Class</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.class_label}</Text>
                  </View>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Questions</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.question_count}</Text>
                  </View>
                </View>
              </View>
            )}

            {omrSet && !omrQuestionCountSupported ? (
              <View style={styles.omrWarningCard}>
                <Text style={styles.omrWarningTitle}>Unsupported question count</Text>
                <Text style={styles.omrWarningText}>
                  This backend only supports OMR sheets with 5, 10, 15, 20, up to 100 questions. Update this set
                  before generating sheets.
                </Text>
              </View>
            ) : null}

            <View style={styles.omrDateSection}>
              <Text style={styles.omrSectionTitle}>Test date</Text>
              <View style={styles.omrDateCard}>
                <View style={styles.omrDateIcon}>
                  <MaterialIcons name="event" size={18} color="#163828" />
                </View>
                <TextInput
                  style={styles.omrDateInput}
                  value={omrTestDate}
                  onChangeText={setOmrTestDate}
                  placeholder="e.g. 20 Apr 2026"
                  placeholderTextColor="#8B948C"
                />
              </View>
            </View>

            <View style={styles.omrStudentsSection}>
              <View style={styles.omrStudentsSectionHeader}>
                <Text style={styles.omrSectionTitle}>Students</Text>
                {!omrStudentsLoading && (
                  <View style={styles.omrStudentCount}>
                    <Text style={styles.omrStudentCountT}>{omrStudents.length}</Text>
                  </View>
                )}
              </View>
              <Text style={styles.omrStudentsSub}>
                One OMR sheet will be generated for each student in this class with their name, roll number, and a
                unique QR code linked to this MCQ set.
              </Text>

              {omrStudentsLoading ? (
                <View style={styles.omrStudentsLoading}>
                  <ActivityIndicator color="#1B5E20" />
                  <Text style={styles.omrLoadingText}>Loading students...</Text>
                </View>
              ) : omrStudents.length === 0 ? (
                <View style={styles.omrNoStudents}>
                  <MaterialIcons name="people-outline" size={40} color="#E0E0E0" />
                  <Text style={styles.omrNoStudentsT}>No students found in this class</Text>
                </View>
              ) : (
                <View style={styles.omrStudentList}>
                  {omrStudents.map((student) => (
                    <View key={student.id} style={styles.omrStudentItem}>
                      <View style={styles.omrStudentAvatar}>
                        <Text style={styles.omrStudentAvatarT}>
                          {(student.full_name || 'S').charAt(0).toUpperCase()}
                        </Text>
                      </View>
                      <View style={styles.omrStudentInfo}>
                        <Text style={styles.omrStudentName}>
                          {student.full_name || `Student ${student.roll_number}`}
                        </Text>
                        <Text style={styles.omrStudentRoll}>Roll: {student.roll_number}</Text>
                      </View>
                      <View
                        style={[
                          styles.omrStudentStatus,
                          { backgroundColor: student.is_active ? '#E8F5E9' : '#FFEBEE' },
                        ]}
                      >
                        <Text
                          style={[
                            styles.omrStudentStatusText,
                            { color: student.is_active ? '#2E7D32' : '#C62828' },
                          ]}
                        >
                          {student.is_active ? 'Active' : 'Inactive'}
                        </Text>
                        <View
                          style={[
                            styles.omrStudentDot,
                            { backgroundColor: student.is_active ? '#4CAF50' : '#EF5350' },
                          ]}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </ScrollView>

          <View style={styles.omrBottomAction}>
            <View style={styles.omrBottomMeta}>
              <Text style={styles.omrBottomMetaLabel}>Ready to export</Text>
              <Text style={styles.omrBottomMetaValue}>
                {omrQuestionCountSupported
                  ? `${omrStudents.length} ${omrStudents.length === 1 ? 'student' : 'students'}`
                  : 'Fix question count first'}
              </Text>
            </View>
            <Pressable
              style={[
                styles.omrGenerateBtn,
                (!omrQuestionCountSupported || omrStudents.length === 0 || omrGenerating) &&
                  styles.omrGenerateBtnDisabled,
              ]}
              onPress={() => void handleGenerateOmr()}
              disabled={!omrQuestionCountSupported || omrStudents.length === 0 || omrGenerating}
            >
              {omrGenerating ? (
                <View style={styles.omrGenRow}>
                  <ActivityIndicator color="white" size="small" />
                  <Text style={styles.omrGenerateBtnT}>Generating PDF...</Text>
                </View>
              ) : (
                <View style={styles.omrGenRow}>
                  <MaterialIcons name="picture-as-pdf" size={20} color="white" />
                  <Text style={styles.omrGenerateBtnT}>
                    GENERATE {omrStudents.length} OMR {omrStudents.length === 1 ? 'SHEET' : 'SHEETS'}
                  </Text>
                </View>
              )}
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );

  if (isDesktop) {
    const selectedDesktopSet = activeSet ?? questionSets[0] ?? null;

    return (
      <View style={styles.desktopRoot}>
        <ScrollView contentContainerStyle={styles.desktopScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.desktopHero}>
            <View style={styles.desktopHeroCopy}>
              <Text style={styles.desktopEyebrow}>Question Library</Text>
              <Text style={styles.desktopTitle}>Sets</Text>
              <Text style={styles.desktopBody}>
                Create papers, manage MCQs, and generate OMR sheets from one workspace.
              </Text>
            </View>

            <View style={styles.desktopHeroActions}>
              <Pressable style={styles.desktopPrimaryBtn} onPress={openSetModal}>
                <MaterialIcons name="add" size={20} color="#F7F4EE" />
                <Text style={styles.desktopPrimaryBtnT}>New Set</Text>
              </Pressable>

              <View style={styles.desktopStatsRow}>
                <View style={styles.desktopStatCard}>
                  <Text style={styles.desktopStatValue}>{totalSets}</Text>
                  <Text style={styles.desktopStatLabel}>Sets</Text>
                </View>
                <View style={styles.desktopStatCard}>
                  <Text style={styles.desktopStatValue}>{totalQuestions}</Text>
                  <Text style={styles.desktopStatLabel}>Questions</Text>
                </View>
                <View style={styles.desktopStatCard}>
                  <Text style={styles.desktopStatValue}>{totalAssignments}</Text>
                  <Text style={styles.desktopStatLabel}>Assignments</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.desktopToolbar}>
            <View style={styles.desktopSearch}>
              <MaterialIcons name="search" size={20} color="#8B948C" />
              <TextInput
                style={styles.desktopSearchInput}
                placeholder="Search by title, subject, or class"
                placeholderTextColor="#8B948C"
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>

            <Text style={styles.desktopCountText}>
              {searchQuery.trim()
                ? `${filteredSets.length} result${filteredSets.length === 1 ? '' : 's'}`
                : `${questionSets.length} sets`}
            </Text>
          </View>

          <View style={styles.desktopGrid}>
            <View style={styles.desktopListPane}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>All sets</Text>
                <Text style={styles.sectionSub}>Pick a set to review questions and actions.</Text>
              </View>

              {loading ? (
                <View style={styles.loadingBlock}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : filteredSets.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <View style={styles.emptyIconWrap}>
                    <MaterialIcons name="auto-stories" size={36} color="#163828" />
                  </View>
                  <Text style={styles.emptyT}>No sets found</Text>
                  <Text style={styles.emptyBody}>Create a set or change the search terms.</Text>
                  <Pressable style={styles.emptyBtn} onPress={openSetModal}>
                    <Text style={styles.emptyBtnT}>Create set</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.desktopSetGrid}>
                  {filteredSets.map((item) => {
                    const isSelected = selectedSetId === item.id;
                    return (
                      <Pressable
                        key={item.id}
                        onPress={() => setSelectedSetId(item.id)}
                        style={[styles.desktopSetCard, isSelected && styles.desktopSetCardActive]}
                      >
                        <View style={styles.desktopSetHead}>
                          <View style={styles.desktopSetBadge}>
                            <MaterialIcons name="library-books" size={18} color="#163828" />
                          </View>
                          <Text style={styles.desktopSetChip}>{item.question_count} questions</Text>
                        </View>

                        <Text style={styles.desktopSetTitle}>{item.title}</Text>
                        <Text style={styles.desktopSetStatusText}>{formatQuestionSetStatus(item.status)}</Text>
                        <Text style={styles.desktopSetMeta}>
                          {item.subject_label} · {item.class_label}
                        </Text>
                        {getTransferredSetNote(item, profile?.id) ? (
                          <Text style={styles.desktopSetOwnerNote}>
                            {getTransferredSetNote(item, profile?.id)}
                          </Text>
                        ) : null}

                        <View style={styles.desktopSetActions}>
                          <Pressable style={styles.desktopSetSecondary} onPress={() => void openOmrModal(item)}>
                            <MaterialIcons name="qr-code-2" size={16} color="#0D47A1" />
                            <Text style={styles.desktopSetSecondaryT}>OMR</Text>
                          </Pressable>
                          <Pressable style={styles.desktopSetPrimary} onPress={() => openAddMcqModal(item.id)}>
                            <MaterialIcons name="playlist-add" size={16} color="#F7F4EE" />
                            <Text style={styles.desktopSetPrimaryT}>Manage MCQs</Text>
                          </Pressable>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>

            <View style={styles.desktopDetailPane}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Selected set</Text>
                <Text style={styles.sectionSub}>Review the active paper and question bank.</Text>
              </View>

              {selectedDesktopSet ? (
                <>
                  <View style={styles.desktopDetailCard}>
                    <View style={styles.desktopDetailTop}>
                      <View style={styles.desktopDetailIcon}>
                        <MaterialIcons name="article" size={20} color="#163828" />
                      </View>
                      <Text style={styles.desktopDetailChip}>{selectedDesktopSet.question_count} questions</Text>
                    </View>
                    <Text style={styles.desktopDetailTitle}>{selectedDesktopSet.title}</Text>
                    <Text style={styles.desktopDetailStatusText}>
                      {formatQuestionSetStatus(selectedDesktopSet.status)}
                    </Text>
                    <Text style={styles.desktopDetailMeta}>
                      {selectedDesktopSet.subject_label} · {selectedDesktopSet.class_label}
                    </Text>
                    {getTransferredSetNote(selectedDesktopSet, profile?.id) ? (
                      <Text style={styles.desktopDetailOwnerNote}>
                        {getTransferredSetNote(selectedDesktopSet, profile?.id)}
                      </Text>
                    ) : null}
                    <Text style={styles.desktopDetailBody}>
                      {selectedDesktopSet.description?.trim() ||
                        'No description was added for this set.'}
                    </Text>

                    <View style={styles.desktopDetailActions}>
                      <Pressable style={styles.desktopDetailSecondary} onPress={() => void openOmrModal(selectedDesktopSet)}>
                        <Text style={styles.desktopDetailSecondaryT}>Generate OMR</Text>
                      </Pressable>
                      <Pressable style={styles.desktopDetailPrimary} onPress={() => openAddMcqModal(selectedDesktopSet.id)}>
                        <Text style={styles.desktopDetailPrimaryT}>Manage MCQs</Text>
                      </Pressable>
                    </View>
                  </View>

                  <View style={styles.desktopQuestionPane}>
                    <View style={styles.sectionHead}>
                      <Text style={styles.sectionTitle}>Question bank</Text>
                      <Text style={styles.sectionSub}>
                        {questionLoading ? 'Loading questions...' : `${questions.length} loaded`}
                      </Text>
                    </View>

                    {questionLoading ? (
                      <View style={styles.loadingBlock}>
                        <ActivityIndicator color="#163828" />
                      </View>
                    ) : questions.length === 0 ? (
                      <View style={styles.emptyContainer}>
                        <View style={styles.emptyIconWrap}>
                          <MaterialIcons name="quiz" size={36} color="#163828" />
                        </View>
                        <Text style={styles.emptyT}>No questions yet</Text>
                        <Text style={styles.emptyBody}>Manage MCQs to start building the set.</Text>
                        <Pressable style={styles.emptyBtn} onPress={() => openAddMcqModal(selectedDesktopSet.id)}>
                          <Text style={styles.emptyBtnT}>Manage MCQs</Text>
                        </Pressable>
                      </View>
                    ) : (
                      <View style={styles.desktopQuestionList}>
                        {questions.map((question, index) => (
                          <View key={question.id} style={styles.desktopQuestionCard}>
                            <View style={styles.desktopQuestionHeader}>
                              <Text style={styles.desktopQuestionIndex}>Question {index + 1}</Text>
                              <Text style={styles.desktopQuestionMarks}>{question.marks} mark</Text>
                            </View>
                            <Text style={styles.desktopQuestionPrompt}>{question.prompt}</Text>
                            <View style={styles.desktopQuestionOptions}>
                              {(['A', 'B', 'C', 'D'] as const).map((option) => {
                                const value =
                                  option === 'A'
                                    ? question.option_a
                                    : option === 'B'
                                      ? question.option_b
                                      : option === 'C'
                                        ? question.option_c
                                        : question.option_d;
                                return (
                                  <View
                                    key={option}
                                    style={[
                                      styles.desktopOptionRow,
                                      question.correct_option === option && styles.desktopOptionRowActive,
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.desktopOptionLabel,
                                        question.correct_option === option && styles.desktopOptionLabelActive,
                                      ]}
                                    >
                                      {option}
                                    </Text>
                                    <Text style={styles.desktopOptionValue}>{value}</Text>
                                  </View>
                                );
                              })}
                            </View>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                </>
              ) : (
                <View style={styles.emptyDesktopDetail}>
                  <View style={styles.emptyIconWrap}>
                    <MaterialIcons name="auto-stories" size={36} color="#163828" />
                  </View>
                  <Text style={styles.emptyT}>No set selected</Text>
                  <Text style={styles.emptyBody}>Select a set on the left to see the details here.</Text>
                </View>
              )}
            </View>
          </View>
        </ScrollView>

        {sharedModals}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.heroSection}>
        <View style={styles.heroRow}>
          <View style={styles.heroCopy}>
            <Text style={styles.heroSub}>Question Library</Text>
            <Text style={styles.heroMain}>Build and manage every paper from one surface.</Text>
            <Text style={styles.heroBody}>
              Search sets, open question banks, and prepare OMR-ready papers without bouncing between screens.
            </Text>
          </View>
        </View>

        <View style={styles.heroPanel}>
          <View style={styles.heroPanelTop}>
            <Text style={styles.heroPanelLabel}>Library controls</Text>
            <Pressable style={styles.heroAddBtn} onPress={openSetModal}>
              <MaterialIcons name="add" size={22} color="#163828" />
              <Text style={styles.heroAddText}>New Set</Text>
            </Pressable>
          </View>

          <View style={styles.heroSearch}>
            <MaterialIcons name="search" size={20} color="#BBD0C2" />
            <TextInput
              style={styles.heroInput}
              placeholder="Search by title, subject, or class"
              placeholderTextColor="#BBD0C2"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>

          <View style={styles.heroStatsRow}>
            <View style={styles.heroStatsBox}>
              <Text style={styles.heroStatsTitle}>{totalSets}</Text>
              <Text style={styles.heroStatsLabel}>Sets</Text>
            </View>
            <View style={styles.heroStatsDivider} />
            <View style={styles.heroStatsBox}>
              <Text style={styles.heroStatsTitle}>{totalQuestions}</Text>
              <Text style={styles.heroStatsLabel}>Questions</Text>
            </View>
            <View style={styles.heroStatsDivider} />
            <View style={styles.heroStatsBox}>
              <Text style={styles.heroStatsTitle}>{totalAssignments}</Text>
              <Text style={styles.heroStatsLabel}>Assignments</Text>
            </View>
          </View>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.screenScroll} showsVerticalScrollIndicator={false}>
        <View style={styles.contentSurface}>
          <View style={styles.listHeader}>
            <Text style={styles.listTitle}>Your sets</Text>
            <Text style={styles.listSub}>
              {searchQuery.trim()
                ? `${filteredSets.length} results for "${searchQuery.trim()}"`
                : 'Open any set to review questions or generate OMR sheets.'}
            </Text>
          </View>

          <View style={styles.setsList}>
            {loading ? (
              <View style={styles.loadingBlock}>
                <ActivityIndicator color="#163828" />
              </View>
            ) : filteredSets.length === 0 ? (
              <View style={styles.emptyContainer}>
                <View style={styles.emptyIconWrap}>
                  <MaterialIcons name="auto-stories" size={36} color="#163828" />
                </View>
                <Text style={styles.emptyT}>No sets found</Text>
                <Text style={styles.emptyBody}>
                  Create a fresh paper or refine your search to find an existing set.
                </Text>
                <Pressable style={styles.emptyBtn} onPress={openSetModal}>
                  <Text style={styles.emptyBtnT}>Create set</Text>
                </Pressable>
              </View>
            ) : (
              filteredSets.map((item) => {
                const isExpanded = selectedSetId === item.id;
                return (
                  <View key={item.id} style={[styles.superCard, isExpanded && styles.superCardActive]}>
                    <Pressable
                      style={styles.superHeader}
                      onPress={() => setSelectedSetId(isExpanded ? null : item.id)}
                    >
                      <View style={styles.superHeaderTop}>
                        <View style={styles.superIconBox}>
                          <MaterialIcons name="library-books" size={20} color="#163828" />
                        </View>
                        <View style={styles.superHeaderMeta}>
                          <Text style={styles.superChip}>{item.question_count} questions</Text>
                          <MaterialIcons
                            name={isExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                            size={22}
                            color="#6F7D71"
                          />
                        </View>
                      </View>

                      <View style={styles.superInfo}>
                        <Text style={styles.superTitle}>{item.title}</Text>
                        <Text style={styles.superStatusText}>{formatQuestionSetStatus(item.status)}</Text>
                        <Text style={styles.superMeta}>
                          {item.subject_label} · {item.class_label}
                        </Text>
                        {getTransferredSetNote(item, profile?.id) ? (
                          <Text style={styles.superOwnerNote}>
                            {getTransferredSetNote(item, profile?.id)}
                          </Text>
                        ) : null}
                      </View>
                    </Pressable>

                    <View style={styles.setFooter}>
                      <Pressable style={styles.secondaryAction} onPress={() => void openOmrModal(item)}>
                        <MaterialIcons name="qr-code-2" size={16} color="#0D47A1" />
                        <Text style={styles.secondaryActionText}>Generate OMR</Text>
                      </Pressable>
                      <Pressable style={styles.primaryAction} onPress={() => openAddMcqModal(item.id)}>
                        <MaterialIcons name="playlist-add" size={18} color="#F7F4EE" />
                        <Text style={styles.primaryActionText}>Manage MCQs</Text>
                      </Pressable>
                    </View>

                    {isExpanded && (
                      <View style={styles.superExpansion}>
                        <View style={styles.expansionActions}>
                          <Text style={styles.expansionT}>Question bank</Text>
                          <Text style={styles.expansionCount}>
                            {questionLoading ? 'Loading...' : `${questions.length} loaded`}
                          </Text>
                        </View>

                        {questionLoading ? (
                          <ActivityIndicator color="#1B5E20" style={{ marginVertical: 20 }} />
                        ) : questions.length === 0 ? (
                          <View style={styles.innerEmpty}>
                            <Text style={styles.innerEmptyT}>No questions yet.</Text>
                          </View>
                        ) : (
                          <View style={styles.innerList}>
                            {questions.map((q, idx) => (
                              <View key={q.id} style={styles.fullQCard}>
                                <View style={styles.fqHeader}>
                                  <Text style={styles.fqNum}>Question {idx + 1}</Text>
                                  <Text style={styles.fqMarks}>{q.marks} Mark</Text>
                                </View>
                                <Text style={styles.fqPrompt}>{q.prompt}</Text>
                                <View style={styles.fqOptions}>
                                  {(['A', 'B', 'C', 'D'] as const).map((option) => {
                                    const value =
                                      option === 'A'
                                        ? q.option_a
                                        : option === 'B'
                                          ? q.option_b
                                          : option === 'C'
                                            ? q.option_c
                                            : q.option_d;
                                    return (
                                      <View
                                        key={option}
                                        style={[styles.fqOpt, q.correct_option === option && styles.fqOptC]}
                                      >
                                        <Text
                                          style={[
                                            styles.fqOptL,
                                            q.correct_option === option && styles.fqOptLC,
                                          ]}
                                        >
                                          {option}
                                        </Text>
                                        <Text style={styles.fqOptV}>{value}</Text>
                                      </View>
                                    );
                                  })}
                                </View>
                              </View>
                            ))}
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                );
              })
            )}
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={setModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSetModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setSetModalVisible(false)} />
          <View style={styles.bsSheet}>
            <View style={styles.bsHandle} />

            <View style={styles.modalHeader}>
              <View style={styles.modalHeaderIcon}>
                <MaterialIcons name="note-add" size={22} color="#163828" />
              </View>
              <View style={styles.modalHeaderCopy}>
                <Text style={styles.modalEyebrow}>Create a paper</Text>
                <Text style={styles.modalTitle}>New Set</Text>
                <Text style={styles.modalSubtitle}>
                  Give the paper a title, describe the purpose, and attach it to a class and subject.
                </Text>
              </View>
            </View>

            <View style={styles.modalSection}>
              <Text style={styles.modalSectionLabel}>Set details</Text>
              <InputField label="TITLE" value={setTitle} onChangeText={setSetTitle} />
              <InputField
                label="DESCRIPTION"
                value={setDescription}
                onChangeText={setSetDescription}
                multiline
              />
            </View>

            <View style={styles.modalSection}>
              <View style={styles.modalSectionHead}>
                <Text style={styles.modalSectionLabel}>Assignment</Text>
                <Text style={styles.modalSectionHint}>Choose the class and subject for this set.</Text>
              </View>
              <View style={styles.assGrid}>
                {assignmentOptions.map((option) => {
                  const isSelected = selectedAssignmentKey === option.key;
                  return (
                    <Pressable
                      key={option.key}
                      onPress={() => setSelectedAssignmentKey(option.key)}
                      style={[styles.assBtn, isSelected && styles.assBtnA]}
                    >
                      <MaterialIcons
                        name={isSelected ? 'check-circle' : 'radio-button-unchecked'}
                        size={16}
                        color={isSelected ? '#163828' : '#8A968B'}
                      />
                      <Text style={[styles.assBtnT, isSelected && styles.assBtnTA]}>{option.label}</Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            {selectedAssignment && (
              <View style={styles.selectionCard}>
                <Text style={styles.selectionLabel}>Selected assignment</Text>
                <Text style={styles.selectionTitle}>{selectedAssignment.class_label}</Text>
                <Text style={styles.selectionMeta}>{selectedAssignment.subject_label}</Text>
              </View>
            )}

            <View style={styles.bsActions}>
              <Pressable style={styles.bsCancel} onPress={() => setSetModalVisible(false)}>
                <Text style={styles.bsCancelT}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.bsSave, submittingSet && styles.disabledButton]}
                onPress={() => void handleCreateSet()}
                disabled={submittingSet}
              >
                {submittingSet ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.bsSaveT}>Create set</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={addMcqModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setAddMcqModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setAddMcqModalVisible(false)} />
          {batchBuilderContent}
        </View>
      </Modal>

      <Modal visible={omrModalVisible} animationType="slide">
        <View style={styles.omrContainer}>
          <View style={styles.omrHeader}>
            <Pressable onPress={() => setOmrModalVisible(false)} style={styles.omrCloseBtn}>
              <MaterialIcons name="close" size={24} color="#1B5E20" />
            </Pressable>
            <View style={styles.omrHeaderCenter}>
              <Text style={styles.omrHeaderTitle}>Generate OMR Sheets</Text>
              <Text style={styles.omrHeaderSub}>Prepare the final roster before export.</Text>
            </View>
            <View style={{ width: 40 }} />
          </View>

          <ScrollView contentContainerStyle={styles.omrScroll} showsVerticalScrollIndicator={false}>
            <View style={styles.omrHeroPanel}>
              <Text style={styles.omrHeroEyebrow}>Print-ready export</Text>
              <Text style={styles.omrHeroTitle}>
                {omrSet ? omrSet.title : 'MCQ set'}
              </Text>
              <Text style={styles.omrHeroCopy}>
                Each student gets a personalized sheet with QR data, roll number, and the question count tied to this set.
              </Text>
            </View>

            {omrSet && (
              <View style={styles.omrInfoCard}>
                <View style={styles.omrInfoRow}>
                  <View style={styles.omrInfoIcon}>
                    <MaterialIcons name="description" size={20} color="#1B5E20" />
                  </View>
                  <View style={styles.omrInfoText}>
                    <Text style={styles.omrInfoLabel}>MCQ Set</Text>
                    <Text style={styles.omrInfoValue}>{omrSet.title}</Text>
                  </View>
                </View>
                <View style={styles.omrInfoDivider} />
                <View style={styles.omrInfoGrid}>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Subject</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.subject_label}</Text>
                  </View>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Class</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.class_label}</Text>
                  </View>
                  <View style={styles.omrInfoGridItem}>
                    <Text style={styles.omrInfoGridLabel}>Questions</Text>
                    <Text style={styles.omrInfoGridValue}>{omrSet.question_count}</Text>
                  </View>
                </View>
              </View>
            )}

            <View style={styles.omrDateSection}>
              <Text style={styles.omrSectionTitle}>Test date</Text>
              <View style={styles.omrDateCard}>
                <View style={styles.omrDateIcon}>
                  <MaterialIcons name="event" size={18} color="#163828" />
                </View>
                <TextInput
                  style={styles.omrDateInput}
                  value={omrTestDate}
                  onChangeText={setOmrTestDate}
                  placeholder="e.g. 20 Apr 2026"
                  placeholderTextColor="#8B948C"
                />
              </View>
            </View>

            <View style={styles.omrStudentsSection}>
              <View style={styles.omrStudentsSectionHeader}>
                <Text style={styles.omrSectionTitle}>Students</Text>
                {!omrStudentsLoading && (
                  <View style={styles.omrStudentCount}>
                    <Text style={styles.omrStudentCountT}>{omrStudents.length}</Text>
                  </View>
                )}
              </View>
              <Text style={styles.omrStudentsSub}>
                One OMR sheet will be generated for each student in this class with their name, roll number, and a unique QR code linked to this MCQ set.
              </Text>

              {omrStudentsLoading ? (
                <View style={styles.omrStudentsLoading}>
                  <ActivityIndicator color="#1B5E20" />
                  <Text style={styles.omrLoadingText}>Loading students...</Text>
                </View>
              ) : omrStudents.length === 0 ? (
                <View style={styles.omrNoStudents}>
                  <MaterialIcons name="people-outline" size={40} color="#E0E0E0" />
                  <Text style={styles.omrNoStudentsT}>No students found in this class</Text>
                </View>
              ) : (
                <View style={styles.omrStudentList}>
                  {omrStudents.map((student) => (
                    <View key={student.id} style={styles.omrStudentItem}>
                      <View style={styles.omrStudentAvatar}>
                        <Text style={styles.omrStudentAvatarT}>
                          {(student.full_name || 'S').charAt(0).toUpperCase()}
                        </Text>
                      </View>
                      <View style={styles.omrStudentInfo}>
                        <Text style={styles.omrStudentName}>
                          {student.full_name || `Student ${student.roll_number}`}
                        </Text>
                        <Text style={styles.omrStudentRoll}>Roll: {student.roll_number}</Text>
                      </View>
                      <View
                        style={[
                          styles.omrStudentStatus,
                          { backgroundColor: student.is_active ? '#E8F5E9' : '#FFEBEE' },
                        ]}
                      >
                        <Text
                          style={[
                            styles.omrStudentStatusText,
                            { color: student.is_active ? '#2E7D32' : '#C62828' },
                          ]}
                        >
                          {student.is_active ? 'Active' : 'Inactive'}
                        </Text>
                        <View
                          style={[
                            styles.omrStudentDot,
                            { backgroundColor: student.is_active ? '#4CAF50' : '#EF5350' },
                          ]}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </ScrollView>

          <View style={styles.omrBottomAction}>
            <View style={styles.omrBottomMeta}>
              <Text style={styles.omrBottomMetaLabel}>Ready to export</Text>
              <Text style={styles.omrBottomMetaValue}>
                {omrStudents.length} {omrStudents.length === 1 ? 'student' : 'students'}
              </Text>
            </View>
            <Pressable
              style={[
                styles.omrGenerateBtn,
                (omrStudents.length === 0 || omrGenerating) && styles.omrGenerateBtnDisabled,
              ]}
              onPress={() => void handleGenerateOmr()}
              disabled={omrStudents.length === 0 || omrGenerating}
            >
              {omrGenerating ? (
                <View style={styles.omrGenRow}>
                  <ActivityIndicator color="white" size="small" />
                  <Text style={styles.omrGenerateBtnT}>Generating PDF...</Text>
                </View>
              ) : (
                <View style={styles.omrGenRow}>
                  <MaterialIcons name="picture-as-pdf" size={20} color="white" />
                  <Text style={styles.omrGenerateBtnT}>
                    GENERATE {omrStudents.length} OMR {omrStudents.length === 1 ? 'SHEET' : 'SHEETS'}
                  </Text>
                </View>
              )}
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function InputField(props: React.ComponentProps<typeof TextInput> & { label: string }) {
  return (
    <View style={styles.fld}>
      <Text style={styles.fldL}>{props.label}</Text>
      <TextInput
        placeholderTextColor="#C8D4C8"
        style={[styles.fldI, props.multiline && styles.fldM]}
        textAlignVertical={props.multiline ? 'top' : 'center'}
        {...props}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#103427',
  },
  desktopRoot: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScroll: {
    padding: 24,
    gap: 20,
  },
  desktopHero: {
    backgroundColor: '#103427',
    borderRadius: 30,
    padding: 24,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
  },
  desktopHeroCopy: {
    flex: 1,
    gap: 10,
    maxWidth: 620,
  },
  desktopEyebrow: {
    color: '#A6BBAE',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#F7F4EE',
    fontSize: 38,
    lineHeight: 44,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 22,
    maxWidth: 560,
  },
  desktopHeroActions: {
    minWidth: 360,
    alignItems: 'stretch',
    gap: 14,
  },
  desktopPrimaryBtn: {
    backgroundColor: '#F4EFE6',
    borderRadius: 18,
    minHeight: 52,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  desktopPrimaryBtnT: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
  },
  desktopStatsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  desktopStatCard: {
    flex: 1,
    backgroundColor: 'rgba(244, 239, 230, 0.08)',
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.12)',
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 28,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopStatLabel: {
    color: '#A6BBAE',
    fontSize: 11,
    marginTop: 2,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  desktopToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    justifyContent: 'space-between',
  },
  desktopSearch: {
    flex: 1,
    minHeight: 56,
    backgroundColor: '#FBF8F2',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  desktopSearchInput: {
    flex: 1,
    color: '#163828',
    fontSize: 15,
    fontWeight: '600',
  },
  desktopCountText: {
    color: '#6F7D71',
    fontSize: 13,
    fontWeight: '700',
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopListPane: {
    flex: 1,
    gap: 14,
  },
  desktopDetailPane: {
    width: 410,
    gap: 14,
  },
  sectionHead: {
    gap: 4,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 22,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionSub: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  desktopSetGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  desktopSetCard: {
    width: '48.5%',
    minWidth: 280,
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#E7DED1',
    padding: 16,
    gap: 12,
  },
  desktopSetCardActive: {
    borderColor: '#163828',
    shadowColor: '#163828',
    shadowOpacity: 0.06,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 2,
  },
  desktopSetHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  desktopSetBadge: {
    width: 40,
    height: 40,
    borderRadius: 14,
    backgroundColor: '#E1E8DE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopSetChip: {
    color: '#47624F',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  desktopSetTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: '800',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  desktopSetStatusText: {
    marginTop: 2,
    fontSize: 10,
    fontWeight: '800',
    color: '#8A968B',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  desktopSetMeta: {
    fontSize: 13,
    color: '#6F7D71',
  },
  desktopSetOwnerNote: {
    fontSize: 11,
    lineHeight: 15,
    color: '#869387',
  },
  desktopSetActions: {
    flexDirection: 'row',
    gap: 10,
  },
  desktopSetSecondary: {
    flex: 1,
    minHeight: 42,
    borderRadius: 14,
    backgroundColor: '#E8F1F8',
    borderWidth: 1,
    borderColor: '#C9DEEF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  desktopSetSecondaryT: {
    color: '#0D47A1',
    fontSize: 12,
    fontWeight: '800',
  },
  desktopSetPrimary: {
    flex: 1,
    minHeight: 42,
    borderRadius: 14,
    backgroundColor: '#163828',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  desktopSetPrimaryT: {
    color: '#F7F4EE',
    fontSize: 12,
    fontWeight: '800',
  },
  desktopDetailCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#E7DED1',
    padding: 18,
    gap: 12,
  },
  desktopDetailTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  desktopDetailIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#E1E8DE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopDetailChip: {
    color: '#47624F',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  desktopDetailTitle: {
    fontSize: 24,
    lineHeight: 30,
    fontWeight: '900',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  desktopDetailStatusText: {
    marginTop: -2,
    fontSize: 10,
    fontWeight: '800',
    color: '#8A968B',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  desktopDetailMeta: {
    fontSize: 13,
    color: '#6F7D71',
  },
  desktopDetailOwnerNote: {
    fontSize: 11,
    lineHeight: 16,
    color: '#7A877C',
  },
  desktopDetailBody: {
    fontSize: 13,
    lineHeight: 19,
    color: '#506055',
  },
  desktopDetailActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  desktopDetailSecondary: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    backgroundColor: '#E8F1F8',
    borderWidth: 1,
    borderColor: '#C9DEEF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopDetailSecondaryT: {
    color: '#0D47A1',
    fontSize: 12,
    fontWeight: '800',
  },
  desktopDetailPrimary: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopDetailPrimaryT: {
    color: '#F7F4EE',
    fontSize: 12,
    fontWeight: '800',
  },
  desktopQuestionPane: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#E7DED1',
    padding: 18,
    gap: 14,
  },
  desktopQuestionList: {
    gap: 12,
  },
  desktopQuestionCard: {
    backgroundColor: '#F6F1E8',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
    padding: 14,
    gap: 10,
  },
  desktopQuestionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  desktopQuestionIndex: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  desktopQuestionMarks: {
    color: '#6F7D71',
    fontSize: 12,
    fontWeight: '700',
  },
  desktopQuestionPrompt: {
    color: '#163828',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '600',
  },
  desktopQuestionOptions: {
    gap: 8,
  },
  desktopOptionRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 14,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  desktopOptionRowActive: {
    borderColor: '#163828',
    backgroundColor: '#EDF4EF',
  },
  desktopOptionLabel: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#E1E8DE',
    color: '#163828',
    textAlign: 'center',
    textAlignVertical: 'center',
    fontSize: 12,
    fontWeight: '800',
    overflow: 'hidden',
  },
  desktopOptionLabelActive: {
    backgroundColor: '#163828',
    color: '#F7F4EE',
  },
  desktopOptionValue: {
    flex: 1,
    color: '#33443A',
    fontSize: 13,
    lineHeight: 18,
  },
  emptyDesktopDetail: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#E7DED1',
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 320,
    gap: 10,
  },
  desktopDetailPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroSection: {
    backgroundColor: '#103427',
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 24,
  },
  heroRow: {
    gap: 12,
    marginBottom: 20,
  },
  heroCopy: {
    maxWidth: 340,
    gap: 8,
  },
  heroSub: {
    color: '#A6BBAE',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '700',
  },
  heroMain: {
    color: '#F7F4EE',
    fontSize: 31,
    lineHeight: 37,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
  },
  heroAddBtn: {
    backgroundColor: '#F4EFE6',
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  heroAddText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '700',
  },
  heroPanel: {
    backgroundColor: 'rgba(244, 239, 230, 0.07)',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
    padding: 16,
    gap: 16,
  },
  heroPanelTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroPanelLabel: {
    color: '#A6BBAE',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  heroSearch: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(244, 239, 230, 0.06)',
    borderRadius: 16,
    paddingHorizontal: 14,
    height: 50,
  },
  heroInput: {
    flex: 1,
    color: 'white',
    fontSize: 15,
    paddingLeft: 10,
    fontWeight: '600',
  },
  heroStatsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  heroStatsBox: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  heroStatsDivider: {
    width: 1,
    height: 30,
    backgroundColor: 'rgba(244, 239, 230, 0.12)',
  },
  heroStatsTitle: {
    color: 'white',
    fontSize: 23,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroStatsLabel: {
    color: '#A6BBAE',
    fontSize: 11,
    fontWeight: '700',
  },
  screenScroll: {
    paddingBottom: 24,
  },
  contentSurface: {
    backgroundColor: '#F4EFE6',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    minHeight: 600,
    paddingHorizontal: 20,
    paddingTop: 24,
    gap: 18,
  },
  listHeader: {
    gap: 4,
  },
  listTitle: {
    color: '#163828',
    fontSize: 22,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  listSub: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  setsList: {
    gap: 14,
  },
  loadingBlock: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  superCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#E7DED1',
    overflow: 'hidden',
  },
  superCardActive: {
    borderColor: '#163828',
  },
  superHeader: {
    padding: 18,
    gap: 14,
  },
  superHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  superIconBox: {
    width: 42,
    height: 42,
    borderRadius: 16,
    backgroundColor: '#E1E8DE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  superHeaderMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  superChip: {
    color: '#47624F',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  superInfo: {
    gap: 6,
  },
  superTitle: {
    fontSize: 18,
    lineHeight: 23,
    fontWeight: '800',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  superMeta: {
    fontSize: 13,
    color: '#6F7D71',
  },
  superOwnerNote: {
    fontSize: 11,
    lineHeight: 15,
    color: '#869387',
  },
  superStatusText: {
    marginTop: 2,
    fontSize: 10,
    fontWeight: '800',
    color: '#8A968B',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  setFooter: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 18,
    paddingBottom: 18,
  },
  primaryAction: {
    flex: 1,
    backgroundColor: '#163828',
    borderRadius: 16,
    minHeight: 46,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryActionText: {
    color: '#F7F4EE',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.3,
  },
  secondaryAction: {
    flex: 1,
    backgroundColor: '#E8F1F8',
    borderRadius: 16,
    minHeight: 46,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#C9DEEF',
  },
  secondaryActionText: {
    color: '#0D47A1',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.3,
  },
  superExpansion: {
    backgroundColor: '#F6F1E8',
    borderTopWidth: 1,
    borderTopColor: '#E7DED1',
    padding: 16,
    gap: 14,
  },
  expansionActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  expansionT: {
    fontSize: 12,
    fontWeight: '800',
    color: '#163828',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  expansionCount: {
    fontSize: 12,
    color: '#6F7D71',
    fontWeight: '700',
  },
  innerEmpty: {
    paddingVertical: 26,
    alignItems: 'center',
  },
  innerEmptyT: {
    fontSize: 13,
    color: '#7D887E',
  },
  innerList: {
    gap: 12,
  },
  fullQCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 20,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E9E1D5',
  },
  fqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  fqNum: {
    fontSize: 11,
    fontWeight: '900',
    color: '#163828',
    letterSpacing: 0.7,
  },
  fqMarks: {
    fontSize: 10,
    fontWeight: '800',
    color: '#0D47A1',
    backgroundColor: '#E8F1F8',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 8,
  },
  fqPrompt: {
    fontSize: 14,
    fontWeight: '700',
    color: '#212121',
    lineHeight: 20,
    marginBottom: 12,
  },
  fqOptions: {
    gap: 8,
  },
  fqOpt: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderWidth: 1,
    borderColor: '#EEE6DA',
    borderRadius: 12,
    gap: 10,
    backgroundColor: '#FFFDF9',
  },
  fqOptC: {
    borderColor: '#163828',
    backgroundColor: '#EEF4EE',
  },
  fqOptL: {
    fontSize: 12,
    fontWeight: '900',
    color: '#9E9E9E',
  },
  fqOptLC: {
    color: '#163828',
  },
  fqOptV: {
    fontSize: 13,
    color: '#424242',
    fontWeight: '600',
    flex: 1,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 52,
    gap: 10,
  },
  emptyIconWrap: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyT: {
    fontSize: 20,
    fontWeight: '800',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  emptyBody: {
    maxWidth: 250,
    textAlign: 'center',
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  emptyBtn: {
    marginTop: 8,
    backgroundColor: '#163828',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 999,
  },
  emptyBtnT: {
    color: '#F7F4EE',
    fontWeight: '800',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  bsBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(6, 20, 14, 0.58)',
    justifyContent: 'flex-end',
  },
  modalBackdropDesktop: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  bsSheet: {
    backgroundColor: '#FBF8F2',
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 16,
    maxHeight: '92%',
  },
  bsSheetDesktop: {
    width: '94%',
    maxWidth: 920,
    borderRadius: 28,
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: 22,
    gap: 16,
  },
  bsHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#D8E0D8',
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 10,
  },
  bsTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#1B5E20',
  },
  bsActions: {
    flexDirection: 'row',
    gap: 14,
    marginTop: 10,
  },
  bsCancel: {
    flex: 1,
    height: 50,
    borderRadius: 14,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bsCancelT: {
    color: '#777',
    fontWeight: '800',
  },
  bsSave: {
    flex: 2,
    height: 50,
    borderRadius: 14,
    backgroundColor: '#1B5E20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bsSaveT: {
    color: 'white',
    fontWeight: '900',
  },
  disabledButton: {
    opacity: 0.7,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 4,
  },
  modalHeaderIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeaderCopy: {
    flex: 1,
    gap: 3,
  },
  modalEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  modalTitle: {
    color: '#163828',
    fontSize: 24,
    lineHeight: 29,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  modalSubtitle: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
  },
  modalSection: {
    gap: 10,
  },
  setModalBody: {
    gap: 16,
  },
  setModalBodyDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  setModalColumn: {
    gap: 16,
  },
  setModalColumnPrimary: {
    flex: 1.15,
  },
  setModalColumnSecondary: {
    flex: 0.95,
  },
  modalSectionHead: {
    gap: 4,
  },
  modalSectionLabel: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  modalSectionHint: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  selectionCard: {
    backgroundColor: '#EAF3E7',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#CFE0C7',
    padding: 14,
    gap: 3,
  },
  selectionLabel: {
    color: '#6F7D71',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  selectionTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  selectionMeta: {
    color: '#47624F',
    fontSize: 12,
    fontWeight: '700',
  },
  setSummaryCard: {
    backgroundColor: '#103427',
    borderRadius: 18,
    padding: 16,
    gap: 8,
  },
  setSummaryLabel: {
    color: '#A6BBAE',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  setSummaryText: {
    color: '#F7F4EE',
    fontSize: 13,
    lineHeight: 19,
  },
  batchContainer: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  batchH: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingTop: 58,
    paddingBottom: 18,
    paddingHorizontal: 18,
    backgroundColor: '#163828',
    gap: 14,
  },
  batchCloseBtn: {
    width: 42,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#F4EFE6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  batchHeaderCopy: {
    flex: 1,
    gap: 4,
  },
  batchEyebrow: {
    color: '#A6BBAE',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  batchHT: {
    fontSize: 23,
    fontWeight: '900',
    color: '#F7F4EE',
    fontFamily: Fonts.rounded,
  },
  batchHS: {
    color: '#C4D3C9',
    fontSize: 12,
    lineHeight: 18,
  },
  batchSavePill: {
    minWidth: 66,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#F4EFE6',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  batchContextCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E7DED1',
    gap: 6,
  },
  batchContextTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  batchContextLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#6F7D71',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  batchContextTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  batchContextMeta: {
    fontSize: 12,
    color: '#6F7D71',
  },
  batchContextOwnerNote: {
    fontSize: 11,
    lineHeight: 15,
    color: '#869387',
  },
  batchStatusSection: {
    marginTop: 10,
    gap: 8,
  },
  batchStatusLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#6F7D71',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  batchStatusRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  batchStatusChip: {
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#D6DED2',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  batchStatusChipActive: {
    backgroundColor: '#163828',
    borderColor: '#163828',
  },
  batchStatusChipText: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '800',
  },
  batchStatusChipTextActive: {
    color: '#F7F4EE',
  },
  builderModeCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 20,
    padding: 14,
    borderWidth: 1,
    borderColor: '#E7DED1',
    gap: 10,
  },
  builderModeLabel: {
    fontSize: 11,
    fontWeight: '800',
    color: '#6F7D71',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  builderModeRow: {
    flexDirection: 'row',
    gap: 10,
  },
  builderModeChip: {
    flex: 1,
    minHeight: 44,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE5DC',
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 12,
  },
  builderModeChipActive: {
    backgroundColor: '#163828',
    borderColor: '#163828',
  },
  builderModeChipText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
  },
  builderModeChipTextActive: {
    color: '#F7F4EE',
  },
  aiBuilderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#E3EBE3',
  },
  aiBuilderHead: {
    gap: 4,
  },
  aiBuilderTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  aiBuilderHint: {
    fontSize: 12,
    lineHeight: 18,
    color: '#6F7D71',
  },
  aiFieldRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-end',
  },
  aiFieldCol: {
    width: 132,
  },
  aiFieldColWide: {
    flex: 1,
    gap: 6,
  },
  aiDifficultyRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  aiDifficultyChip: {
    paddingHorizontal: 12,
    height: 38,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DCE5DC',
    backgroundColor: '#F8FBF8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  aiDifficultyChipActive: {
    backgroundColor: '#EAF3E7',
    borderColor: '#163828',
  },
  aiDifficultyChipText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#5D6D60',
  },
  aiDifficultyChipTextActive: {
    color: '#163828',
  },
  aiGenerateBtn: {
    minHeight: 48,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
  },
  aiGenerateBtnText: {
    color: '#F7F4EE',
    fontSize: 13,
    fontWeight: '800',
  },
  batchFeedbackCard: {
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#EEF6EE',
    borderWidth: 1,
    borderColor: '#D8E8D8',
  },
  batchFeedbackText: {
    color: '#163828',
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '700',
  },
  saveBold: {
    color: '#163828',
    fontWeight: '900',
    fontSize: 14,
  },
  batchS: {
    padding: 18,
    gap: 16,
    paddingBottom: 150,
  },
  batchCard: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#EDF2ED',
  },
  bcH: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bcHTWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bcHT: {
    fontSize: 14,
    fontWeight: '900',
    color: '#163828',
  },
  existingQuestionChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#EAF3E7',
    borderWidth: 1,
    borderColor: '#D4E4D0',
  },
  existingQuestionChipText: {
    color: '#163828',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  optRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  optI: {
    width: '48%',
  },
  bcF: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-end',
  },
  correctB: {
    flex: 2,
  },
  marksB: {
    flex: 1,
  },
  miniRow: {
    flexDirection: 'row',
    gap: 6,
  },
  miniBtn: {
    flex: 1,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#EEE',
  },
  miniBtnA: {
    backgroundColor: '#1B5E20',
    borderColor: '#1B5E20',
  },
  miniBtnT: {
    fontSize: 13,
    fontWeight: '800',
    color: '#777',
  },
  miniBtnTA: {
    color: 'white',
  },
  batchPlus: {
    padding: 14,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#163828',
    borderStyle: 'dashed',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.45)',
  },
  batchPlusT: {
    fontSize: 13,
    fontWeight: '900',
    color: '#163828',
  },
  batchLoadingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    paddingVertical: 28,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: '#E3EBE3',
  },
  batchLoadingText: {
    color: '#6F7D71',
    fontSize: 13,
    fontWeight: '700',
  },
  batchAction: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
    paddingBottom: 34,
    backgroundColor: '#F4EFE6',
    borderTopWidth: 1,
    borderTopColor: '#E2D8CA',
  },
  batchBtn: {
    backgroundColor: '#163828',
    height: 54,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  batchBtnT: {
    color: 'white',
    fontSize: 14,
    fontWeight: '900',
  },
  fld: {
    gap: 6,
  },
  fldL: {
    fontSize: 10,
    fontWeight: '900',
    color: '#1B5E20',
    letterSpacing: 0.5,
  },
  fldI: {
    height: 46,
    borderRadius: 12,
    borderWidth: 1.2,
    borderColor: '#EDF2ED',
    backgroundColor: '#FAFCFA',
    paddingHorizontal: 14,
    fontSize: 14,
  },
  fldM: {
    height: 70,
    paddingVertical: 10,
  },
  assGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  assBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 14,
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#EEE',
  },
  assBtnA: {
    backgroundColor: '#EAF3E7',
    borderColor: '#163828',
  },
  assBtnT: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
    color: '#667167',
    flexShrink: 1,
  },
  assBtnTA: {
    color: '#163828',
    fontWeight: '800',
  },
  omrContainer: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  omrHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 60,
    paddingBottom: 18,
    paddingHorizontal: 20,
    backgroundColor: '#F4EFE6',
    gap: 12,
  },
  omrCloseBtn: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrHeaderCenter: {
    flex: 1,
    alignItems: 'center',
  },
  omrHeaderTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#163828',
    fontFamily: Fonts.rounded,
  },
  omrHeaderSub: {
    fontSize: 12,
    color: '#6F7D71',
    marginTop: 2,
    fontWeight: '600',
  },
  omrScroll: {
    padding: 20,
    gap: 20,
    paddingBottom: 150,
  },
  omrHeroPanel: {
    backgroundColor: '#163828',
    borderRadius: 24,
    padding: 18,
    gap: 8,
  },
  omrHeroEyebrow: {
    color: '#A6BBAE',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  omrHeroTitle: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 29,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  omrHeroCopy: {
    color: '#C4D3C9',
    fontSize: 13,
    lineHeight: 19,
  },
  omrInfoCard: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    padding: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  omrInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  omrInfoIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrInfoText: {
    flex: 1,
  },
  omrInfoLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#6F7D71',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  omrInfoValue: {
    fontSize: 16,
    fontWeight: '800',
    color: '#163828',
    marginTop: 2,
  },
  omrInfoDivider: {
    height: 1,
    backgroundColor: '#E7DED1',
    marginVertical: 14,
  },
  omrInfoGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  omrInfoGridItem: {
    flex: 1,
    backgroundColor: '#F6F1E8',
    borderRadius: 12,
    padding: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  omrInfoGridLabel: {
    fontSize: 9,
    fontWeight: '800',
    color: '#9E9E9E',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  omrInfoGridValue: {
    fontSize: 13,
    fontWeight: '800',
    color: '#163828',
    marginTop: 4,
  },
  omrWarningCard: {
    borderRadius: 18,
    padding: 14,
    backgroundColor: '#FFF4E5',
    borderWidth: 1,
    borderColor: '#F2C47C',
    gap: 6,
  },
  omrWarningTitle: {
    fontSize: 12,
    fontWeight: '900',
    color: '#8A4B00',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  omrWarningText: {
    fontSize: 13,
    lineHeight: 18,
    color: '#8A4B00',
    fontWeight: '600',
  },
  omrDateSection: {
    gap: 8,
  },
  omrSectionTitle: {
    fontSize: 13,
    fontWeight: '900',
    color: '#163828',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  omrDateCard: {
    minHeight: 56,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
    backgroundColor: '#FBF8F2',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  omrDateIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrDateInput: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  omrStudentsSection: {
    gap: 12,
  },
  omrStudentsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  omrStudentCount: {
    minWidth: 34,
    height: 30,
    backgroundColor: '#163828',
    paddingHorizontal: 8,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrStudentCountT: {
    fontSize: 11,
    fontWeight: '900',
    color: 'white',
  },
  omrStudentsSub: {
    fontSize: 13,
    color: '#6F7D71',
    lineHeight: 18,
  },
  omrStudentsLoading: {
    padding: 30,
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#FBF8F2',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  omrLoadingText: {
    fontSize: 13,
    color: '#6F7D71',
    fontWeight: '600',
  },
  omrNoStudents: {
    padding: 30,
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#FBF8F2',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  omrNoStudentsT: {
    fontSize: 13,
    color: '#889189',
    fontWeight: '700',
  },
  omrStudentList: {
    gap: 10,
  },
  omrStudentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FBF8F2',
    borderRadius: 18,
    padding: 14,
    gap: 12,
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  omrStudentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 14,
    backgroundColor: '#E5EADD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrStudentAvatarT: {
    fontSize: 15,
    fontWeight: '800',
    color: '#163828',
  },
  omrStudentInfo: {
    flex: 1,
  },
  omrStudentName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#163828',
  },
  omrStudentRoll: {
    fontSize: 11,
    fontWeight: '700',
    color: '#7E877F',
    marginTop: 2,
  },
  omrStudentStatus: {
    minWidth: 54,
    height: 28,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    flexDirection: 'row',
    gap: 6,
  },
  omrStudentStatusText: {
    fontSize: 10,
    fontWeight: '800',
  },
  omrStudentDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  omrBottomAction: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
    paddingBottom: 34,
    backgroundColor: '#F4EFE6',
    borderTopWidth: 1,
    borderTopColor: '#E2D8CA',
    gap: 10,
  },
  omrBottomMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  omrBottomMetaLabel: {
    color: '#6F7D71',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  omrBottomMetaValue: {
    color: '#163828',
    fontSize: 14,
    fontWeight: '800',
  },
  omrGenerateBtn: {
    backgroundColor: '#163828',
    height: 58,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  omrGenerateBtnDisabled: {
    backgroundColor: '#9EAAA2',
    opacity: 0.7,
  },
  omrGenerateBtnT: {
    color: 'white',
    fontSize: 14,
    fontWeight: '900',
    letterSpacing: 0.3,
  },
  omrGenRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
});

